import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { ReportingQuery } from '~/QueryHooks/ReportingQuery';
import i18n from '~/components/i18n';
import { useAuth } from '~/routes/components/AuthProvider';
import TableComponent from '~/components/TableComponent';
import dayjs from 'dayjs';
import InfoPopup from '~/components/InfoPopup';
import { StyledHeader } from './style'
import { DownloadService } from '~/api/services/downloadFiles';
import { ReportingService } from '~/api/services/reporting';
import Dialog from '~/components/Dialog';
import { MonthInput, YearInput } from 'semantic-ui-calendar-react';
import TradingChart from './TradingChart';
import { Dropdown } from 'semantic-ui-react';
import { formatNumber, getPrefixData, getTradingColumnName, handleErrors } from '~/utils/helpers';
import { useParams } from 'react-router-dom';
import ExcelExport from '~/components/ExcelExport';
import { FixedPricePrefixType } from '~/api/services/dashboard';

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'show'
  },
  {
    popupMessage: i18n.t('Results'),
    type: 'icon',
    name: 'clipboard outline',
    size: 'large',
    actionType: 'results'
  }
]

const TradingActivities: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const yearFilters = [
    {
      label: t('Year from'),
      key: 'yearFrom'
    },
    {
      label: t('Year to'),
      key: 'yearTo'
    }
  ]

  const monthFilters = [
    {
      label: t('Month from'),
      key: 'monthFrom'
    },
    {
      label: t('Month to'),
      key: 'monthTo'
    }
  ]
  const [dataFiltered, setDataFiltered] = useState({
    yearFrom: '',
    yearTo: '',
    monthFrom: '',
    monthTo: '',
    tradingVolumeTypeId: ''
  })
  const { prefix } = useParams()
  const { systemData: { amountUnit } } = useAuth()
  const [tradingActivities, setTradingActivities] = useState([])
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [month, setMonth] = useState(null)
  const { systemData: { languages, logged: { languageId }, amountVolume, amountPrice } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const priceLabel = `${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit}`
  const tradingPriceColumns = getTradingColumnName(prefix, priceLabel, amountUnit)
  const { volumePriceLabel, priceTitle } = getPrefixData(prefix)

  const fatProteinColumns = prefix === FixedPricePrefixType.FatProtein ? getFatProteinColumns() : []
  const fatProteinTradColumns = prefix === FixedPricePrefixType.FatProtein ? getFatProteinTradColumns() : []

  const tableColumnHeaders = [
    {
      name: t('Delivery month'),
      key: 'month',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: tradingPriceColumns.average.name,
      key: 'avgPrice',
      type: 'no-unit-number'
    },
    ...fatProteinColumns,
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'offeredAmount',
      type: 'number-whole'
    },
    {
      name: t('Max trading amount (in {{amountUnit}})', { amountUnit }),
      key: 'maxAmount',
      type: 'number-whole'
    },
    {
      name: tradingPriceColumns.priceBrackets.name,
      key: 'confirmedAmount',
      type: 'offered-amount'
    },
    {
      name: t('Remaining amount [in {{amountUnit}} / in % ]', { amountUnit }),
      key: 'remainingAmount',
      type: 'remaining-amount'
    },
    {
      name: t('Actions')
    }
  ]

  const tableColumnHeadersActivitie = [
    {
      name: t('Delivery date'),
      key: 'confirmationDate',
      type: 'dateRibbon',
      format: 'DD.MM.YYYY HH:mm',
      info: t('Date and time when your dairy has accepted your offered amount totally, partially or not at all within the trading slot ({{volumePriceLabel}}).', { volumePriceLabel })
    },
    {
      name: tradingPriceColumns.trading.name,
      key: 'price',
      info: tradingPriceColumns.trading.info,
      type: 'no-unit-number'
    },
    ...fatProteinTradColumns,
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number-whole',
      info: t('Offered amount which you have submitted within the trading slot.')
    },
    {
      name: tradingPriceColumns.price.name,
      key: 'confirmedAmount',
      type: 'number-whole',
      info: t('The amount your dairy has actually confirmed (at most equivalent to offered amount).')
    },
    {
      name: t('Offer ID'),
      key: 'uniqueId'
    },
    {
      name: t('Offer date', { amountUnit }),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm',
      info: t('Date and time when you have submitted your offered amount within the trading slot.')
    },
    {
      name: t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: t('Trader'),
      key: 'name'
    }
  ]

  const {
    data: tradingActivityData,
    isLoading: isBusyActivityData,
    isFetching: isFetching,
    isFetchedAfterMount: isFetchedAfterMount,
    refetch: fetchFilteredData
  } = ReportingQuery.getTradingData(
    prefix,
    {
      ...dataFiltered,
      monthFrom: dataFiltered.monthFrom ? dataFiltered.monthFrom : null,
      monthTo: dataFiltered.monthTo ? dataFiltered.monthTo : null
    })

  const {
    data: tradingSettings,
    isLoading: isBusyActivitySettings
  } = ReportingQuery.getTradingSettings(prefix)

  useEffect(() => {
    fetchFilteredData()
  }, [dataFiltered, prefix])

  useEffect(() => {
    fetchTradingLogs()
  }, [])

  async function fetchTradingLogs() {
    try {
      await ReportingService(prefix).getTradingReportActivitie()
    } catch (error) {
      handleErrors(error)
    }
  }

  function getFatProteinColumns() {
    return [
      {
        name: t('Average fat price (€/100kg)'),
        key: 'avgFatPrice',
        type: 'no-unit-number'
      },
      {
        name: t('Average protein price (€/100kg)'),
        key: 'avgProteinPrice',
        type: 'no-unit-number'
      }
    ]
  }

  function getFatProteinTradColumns() {
    return [
      {
        name: t('Fat price (€/100kg)'),
        key: 'fatPrice',
        type: 'no-unit-number'
      },
      {
        name: t('Protein price (€/100kg)'),
        key: 'proteinPrice',
        type: 'no-unit-number'
      }
    ]
  }

  async function handleDownloadTradingActivities(month) {
    const monthFormated = dayjs(month).format('YYYY-MM-DD')
    try {
      await DownloadService.downloadFile(`reporting/${prefix}/trading-activities/offers/export/excel`, 'trading_activitie', { ...dataFiltered, month: monthFormated })
    }
    catch (error) {
      handleErrors(error)
    }
  }

  async function handleShowTradingActivitie(row, type, index) {
    const { month } = row
    setLoadingAction({
      value: true,
      type,
      id: row.month,
      idKey: 'month'
    })
    const monthFormated = dayjs(month).format('YYYY-MM-DD')
    try {
      const response = await ReportingService(prefix).getTradingOffers({ ...dataFiltered, month: monthFormated })
      setTradingActivities(response.data)
      setMonth(month)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      value: false,
      type: '',
      id: -1,
      idKey: ''
    })
  }

  function handleClickAction(row, type, index) {
    switch (type) {
      case 'show':
        return handleShowTradingActivitie(row, type, index)
      case 'results':
        return handleDownloadTradingActivities(row.month)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      rows={tradingActivityData}
      handleClickAction={(row, type, index) => handleClickAction(row, type, index)}
      removePagination
      animated
      loadingAction={loadingAction}
      isFetchingData={isFetching}
      actions={getActions()}
      total={tradingActivityData.length}
    />
  }

  function renderTitle() {
    return <StyledHeader>
      <div className='title'>{t('Reporting - Trading activities')}</div>
      <InfoPopup
        content={t('Detailed overview over the {{volumePriceLabel}} confirmed by the dairy \n\
          (including average {{priceTitle}}) for the according trading months including all trading activities which were carried out.', { volumePriceLabel, priceTitle })}
        styleIcon={{ cursor: 'pointer', fontSize: '1.4em', marginLeft: 5 }}
      />
    </StyledHeader>
  }

  function renderActions() {
    return <>
      <ExcelExport
        data={dataFiltered}
        buttonType={'icon'}
        icon={'file excel outline'}
        fileName={'trading_activities'}
        url={`reporting/${prefix}/trading-activities/export/excel`}
      />
    </>
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderShowModal() {
    return <Dialog
      open={Boolean(month)}
      textClose={t('Close')}
      handleClose={() => setTradingActivities(null)}
      textHeader={dayjs(month).format('MMM YYYY')}
      textContent={<TableComponent
        tableColumnHeaders={tableColumnHeadersActivitie}
        rows={tradingActivities}
        removePagination
        total={tradingActivities.length}
      />}
      type={'dialog'}
      style={{ width: '90%' }}
    />
  }

  function getNameOfMonthFromIndex(monthValue) {
    if (monthValue === '' || isNaN(monthValue)) return ''
    const date = new Date(2009, monthValue - 1, 10)
    const month = date.toLocaleString(dayjs.locale(), { month: 'long' });
    return month
  }

  function renderFilters() {
    const currentYear = new Date().getFullYear()
    const options = tradingSettings?.tradingVolumeTypes || []
    return <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
      {yearFilters.map(yearFilter => {
        return <YearInput
          key={yearFilter.key}
          name='date'
          autoComplete='off'
          placeholder={yearFilter.label}
          onKeyPress={handleKeyPress}
          closable
          clearIcon={'close'}
          minDate={'2004'}
          maxDate={(currentYear + 2).toString()}
          clearable
          style={{ marginRight: 10, marginBottom: 5 }}
          localization={languageLocal.code}
          closeOnMouseLeave={false}
          className={'datePicker'}
          value={dataFiltered[yearFilter.key]}
          iconPosition='left'
          onChange={(event, { name, value }) => setDataFiltered({
            ...dataFiltered,
            [yearFilter.key]: value
          })}
        />
      })}
      {monthFilters.map(monthFilter => {
        return <MonthInput
          key={monthFilter.key}
          name='date'
          autoComplete='off'
          placeholder={monthFilter.label}
          onKeyPress={handleKeyPress}
          closable
          clearIcon={'close'}
          clearable
          dateFormat={'MM'}
          style={{ marginRight: 10, marginBottom: 5 }}
          localization={languageLocal.code}
          closeOnMouseLeave={false}
          className={'datePicker'}
          value={getNameOfMonthFromIndex(dataFiltered[monthFilter.key])}
          iconPosition='left'
          onChange={(event, data) => {
            setDataFiltered({
              ...dataFiltered,
              [monthFilter.key]: parseInt(data.value)
            })
          }}
        />
      })}
      <Dropdown
        selection
        selectOnBlur={false}
        clearable
        options={options.map(volumeType => ({ value: volumeType?.id, text: `${volumeType?.name} (${formatNumber(volumeType?.percent)} %)` }))}
        placeholder={t('Trading volume type')}
        value={dataFiltered.tradingVolumeTypeId}
        onChange={(e, data) => setDataFiltered({
          ...dataFiltered,
          tradingVolumeTypeId: data.value as string
        })}
      />
    </div>
  }

  return <Layout
    segmented
    isBusy={isBusyActivityData || isBusyActivitySettings}
    actions={renderActions()}
    filters={renderFilters()}
    title={renderTitle()}
    actionAlignToTop
  >
    {tradingActivities && renderShowModal()}
    {tradingActivityData && tradingSettings && <TradingChart
      tradingActivityData={tradingActivityData}
      tradingSettings={tradingSettings}
    />}
    {tradingActivityData && renderTable()}
  </Layout>;
};

export default TradingActivities;